import { matchPath } from "react-router";
import { appRoutes } from "@/app/router";
import { AppStore } from "@/store";
import {
  UNTITLED_COLLECTION_TITLE,
  UNTITLED_NOTE_TITLE,
  UNTITLED_TEMPLATE_TITLE,
} from "@/domains/untitled/untitled";
import { parseRoutingPathname } from "@/modules/routing/parsePathname";

/**
 * Derives a meaningful title from a path by extracting IDs and looking up
 * actual entity titles from the store, falling back to path-based names.
 */
export const getPathTitle = (path: string, store: AppStore): string => {
  // Strip search params so matchPath and exact comparisons work correctly
  // (e.g. "/notes/123?settings=zapier-page" → "/notes/123")
  const pathname = parseRoutingPathname(path);

  // Try to extract note ID from path and get the actual note title
  const noteIdMatch = matchPath(
    appRoutes.notesView({ params: { noteId: ":noteId" }, starPattern: true }),
    pathname
  );

  if (noteIdMatch?.params.noteId) {
    const note = store.notes.get(noteIdMatch.params.noteId);
    if (note?.suggestionData?.suggested_primary_label)
      return note.suggestionData.suggested_primary_label;
    if (note) return note.title || UNTITLED_NOTE_TITLE;
  }

  // Try to extract collection ID from path and get the actual collection title
  const collectionIdMatch = matchPath(
    appRoutes.collectionsView({ params: { collectionId: ":collectionId" }, starPattern: true }),
    pathname
  );

  if (collectionIdMatch?.params.collectionId) {
    const collection = store.collections.get(collectionIdMatch.params.collectionId);
    if (collection) return collection.title || UNTITLED_COLLECTION_TITLE;
  }

  // Try to extract template ID from path and get the actual template title
  const templateIdMatch = matchPath(
    appRoutes.templatesView({ params: { templateId: ":templateId" }, starPattern: true }),
    pathname
  );

  if (templateIdMatch?.params.templateId) {
    const template = store.templates.get(templateIdMatch.params.templateId);
    if (template) return template.title || UNTITLED_TEMPLATE_TITLE;
  }

  // Try to extract session ID from path and get the actual session title
  const sessionIdMatch = matchPath(
    appRoutes.sessionsView({ params: { sessionId: ":sessionId" }, starPattern: true }),
    pathname
  );

  if (sessionIdMatch?.params.sessionId) {
    const session = store.sessions.get(sessionIdMatch.params.sessionId);
    if (session?.title) return session.title;
  }

  // Try to extract topic ID from path and get the actual topic title
  const topicIdMatch = matchPath(
    appRoutes.exploreTopicDetail({ params: { topicId: ":topicId" }, starPattern: true }),
    pathname
  );

  if (topicIdMatch?.params.topicId) {
    return "Explore Topic";
  }

  // Handle search pages - show the query string if available.
  // The two blocks below intentionally read from the raw `path` (not `pathname`)
  // because they need the query params to extract the search query and match saved searches.
  if (pathname.startsWith(appRoutes.search({}).path)) {
    try {
      const searchParams = new URLSearchParams(path.split("?")[1] || "");
      const queryString = searchParams.get("q") || searchParams.get("query");
      if (queryString) {
        return `Search: ${queryString}`;
      }
    } catch {
      // If parsing fails, fall back to simple path checking
    }

    // Handle saved search pages - try to get the saved search title
    if (path.includes("?")) {
      try {
        const searchQuery = path.split("?")[1] || "";
        const savedSearch = store.savedSearches.all.find(
          saved => saved.savedSearchQueryString === `?${searchQuery}`
        );
        if (savedSearch?.title) {
          return savedSearch.title;
        }
      } catch {
        // If parsing fails, fall back to simple path checking
      }
    }

    return "Search";
  }

  if (pathname.startsWith(appRoutes.agentSearch({}).path)) {
    try {
      const searchParams = new URLSearchParams(path.split("?")[1] || "");
      const queryString = searchParams.get("q") || searchParams.get("query");
      if (queryString) {
        return `Agentic Search: ${queryString}`;
      }
    } catch {
      // If parsing fails, fall back to simple path checking
    }

    return "Agentic Search";
  }

  // Handle specific page types with exact path matching
  if (pathname === appRoutes.notesList({}).path) return "Notes";
  if (pathname === appRoutes.collections({}).path) return "Collections";
  if (pathname === appRoutes.templatesList({}).path) return "Templates";
  if (pathname === appRoutes.sessionsList({}).path) return "Sessions";
  if (pathname === appRoutes.trash({}).path) return "Trash";
  if (pathname === appRoutes.explore({}).path) return "Explore";
  if (pathname === appRoutes.oauthConsent({}).path) return "OAuth Consent";

  // Handle audio recording routes
  if (pathname === appRoutes.audioRecordings({}).path) return "Audio Recordings";
  if (pathname === appRoutes.audioRecordingCreate({}).path) return "New Audio Recording";
  const audioRecordingAdminMatch = matchPath(
    appRoutes.audioRecordingAdmin({ params: { recordingId: ":recordingId" }, starPattern: true }),
    pathname
  );
  const audioRecordingMatch = matchPath(
    appRoutes.audioRecording({ params: { recordingId: ":recordingId" }, starPattern: true }),
    pathname
  );
  if (audioRecordingAdminMatch?.params.recordingId) {
    return `Recording ${audioRecordingAdminMatch.params.recordingId} Admin`;
  }
  if (audioRecordingMatch?.params.recordingId) {
    return `Recording ${audioRecordingMatch.params.recordingId}`;
  }

  // Handle admin routes
  if (pathname.startsWith(appRoutes.admin({}).path)) return "Admin";

  // Handle debug routes
  if (pathname === appRoutes.debugDb({}).path) return "Debug DB";
  if (pathname.startsWith(appRoutes.debug({}).path)) return "Debug";

  // Handle settings routes
  if (pathname.startsWith(appRoutes.settings({}).path)) return "Settings";

  // Handle integrations routes
  if (pathname.startsWith(appRoutes.integrations({}).path)) return "Integrations";

  // Handle import batch routes using route helpers
  if (
    matchPath(
      appRoutes.importBatchNotes({
        params: { importBatchId: ":importBatchId" },
        starPattern: true,
      }),
      pathname
    )
  ) {
    return "Import Batch - Notes";
  }

  if (
    matchPath(
      appRoutes.importBatchCollections({
        params: { importBatchId: ":importBatchId" },
        starPattern: true,
      }),
      pathname
    )
  ) {
    return "Import Batch - Collections";
  }

  // Handle home and everything routes (redirects to notes)
  if (pathname === appRoutes.home({}).path || pathname === appRoutes.everything({}).path)
    return "Notes";

  return "Not Found";
};
